import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DownloadForm from '../../components/common/DownloadForm'

const CellCX: React.FC = () => {
  const navigate = useNavigate()
  
  // Reading progress
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0)
  
  // Active section highlight
  const [activeId, setActiveId] = useState<string>('exec-summary')

  // Download form state
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  const sections = [
    { id: "exec-summary", labelKey: "CellCX.sections.executiveSummary" },
    { id: "rise-therapy", labelKey: "CellCX.sections.riseTherapy" },
    { id: "operational-challenges", labelKey: "CellCX.sections.operationalChallenges" },
    { id: "flexible-deployment", labelKey: "CellCX.sections.flexibleDeployment" },
    { id: "data-analytics", labelKey: "CellCX.sections.dataAnalytics" },
    { id: "preparing-scale", labelKey: "CellCX.sections.preparingScale" },
    { id: "conclusion", labelKey: "CellCX.sections.conclusion" },
    { id: "references", labelKey: "CellCX.sections.references" },
  ]

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = el.scrollHeight - window.innerHeight + rect.top
      const header = document.getElementById('navigation')
      const headerOffset = header ? header.offsetHeight + 16 : 80
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - headerOffset), 0),
        Math.max(total, 1)
      )
      setProgress((scrolled / Math.max(total, 1)) * 100)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveId(id)
          })
        },
        { rootMargin: "-40% 0px -50% 0px", threshold: 0.01 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [])

  // Smooth scroll to anchors
  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    // Get actual header height dynamically
    const header = document.getElementById('navigation')
    const headerOffset = header ? header.offsetHeight + 16 : 80 // 16px extra padding
    window.scrollTo({ top: el.offsetTop - headerOffset, behavior: "smooth" })
  }

  // Helper UI component
  const MetaItem = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="font-medium text-slate-700">{label}:</span>
      <span>{value}</span>
    </div>
  )

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Reading progress bar */}
      <div className="fixed inset-x-0 top-0 z-50 h-1 bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-[width]"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Hero */}
      <header className="pt-20 pb-10 border-b border-slate-200 bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            White Paper | CellCX
          </div>

          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
            Scaling Cell & Gene Therapy Programs: How Technology Enables Safe, Efficient, and Compliant Workflows
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 text-base md:text-lg">
            Cell and gene therapies, including CAR-T treatments, represent one of the fastest-growing areas of medicine. Yet scaling these programs outside of large academic medical centers remains a challenge due to complex workflows, regulatory requirements, and knowledge gaps.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <MetaItem label="Author" value="EceptionistCX Research Group" />
            <MetaItem label="Published" value="August 25, 2025" />
            <MetaItem label="Length" value="15 min read" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => setDownloadFormOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download PDF
            </button>
            <button
              onClick={() => navigator.share?.({ title: "Scaling Cell & Gene Therapy Programs", url: window.location.href })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              Share
            </button>
          </div>
        </div>
      </header>

      {/* Content & Sidebar */}
      <main ref={contentRef} className="mx-auto max-w-6xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Article */}
        <article className="lg:col-span-8">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className={`scroll-mt-20 ${i !== 0 ? "mt-10" : ""}`}>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {s.id === "exec-summary" && "⭐ Executive Summary"}
                {s.id === "rise-therapy" && "🤖 1. The Rise of Cell & Gene Therapy"}
                {s.id === "operational-challenges" && "📊 2. Operational Challenges"}
                {s.id === "flexible-deployment" && "💡 3. Flexible Deployment of Best Practices"}
                {s.id === "data-analytics" && "⚠️ 4. Data & Analytics in Cell Therapy"}
                {s.id === "preparing-scale" && "🛠️ 5. Preparing for Scale"}
                {s.id === "conclusion" && "✅ 6. Conclusion"}
                {s.id === "references" && "📚 References"}
              </h2>
              <div className="mt-4 prose prose-slate max-w-none">
                {s.id === "exec-summary" && (
                  <>
                    <p>
                      Cell and gene therapies, including CAR-T treatments, represent one of the fastest-growing areas of medicine. Yet scaling these programs outside of large academic medical centers remains a challenge due to complex workflows, regulatory requirements, and knowledge gaps.
                    </p>
                    <br />
                    <p>
                      CellCX provides a flexible digital platform that can be set up to embed structured best practices, SOPs, and workflows from leading programs, or be configured to reflect an organization's unique processes. By enabling compliant operations, knowledge transfer, and analytics-driven planning, CellCX helps health systems scale cell therapy safely and efficiently.
                    </p>
                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="m-0 text-sm"><span className="font-semibold">Key Insight:</span> As therapies move from research into practice, demand for operational excellence grows.</p>
                    </div>
                  </>
                )}
                {s.id === "rise-therapy" && (
                  <>
                    <p>
                      The cell and gene therapy market is growing rapidly:
                    </p>
                    <br />
                    <ul>
                      <li>- The global CAR-T market is expected to expand from $3.5 billion in 2022 to $23 billion by 2030 [1].</li>
                      <li>- There are now over 2,000 active clinical trials worldwide [2].</li>
                    </ul>
                    <br />
                    <p>
                      As therapies move from research into practice, demand for operational excellence grows.
                    </p>
                  </>
                )}
                {s.id === "operational-challenges" && (
                  <>
                    <p>
                      Scaling cell therapy requires managing multiple complex workflows:
                    </p>
                    <br />
                    <ul>
                      <li>- Scheduling manufacturing slots across in-house or outsourced labs.</li>
                      <li>- Ensuring chain-of-custody compliance for each patient sample.</li>
                      <li>- Coordinating patient onboarding, eligibility, and clinical logistics.</li>
                      <li>- Bridging knowledge gaps outside of large academic centers.</li>
                    </ul>
                    <br />
                    <p>
                      Operational bottlenecks can create delays, increase risk, and limit patient access.
                    </p>
                  </>
                )}
                {s.id === "flexible-deployment" && (
                  <>
                    <p>
                      CellCX can be deployed in two modes:
                    </p>
                    <br />
                    <ol>
                      <li>1. Pre-configured with structured best practices, SOPs, workflows, and compliance guidelines developed by leading centers.</li>
                      <li>2. Configured to align with an organization's own workflows and processes.</li>
                    </ol>
                    <br />
                    <p>
                      This flexibility supports both knowledge transfer and organizational autonomy, enabling compliant operations in any environment.
                    </p>
                  </>
                )}
                {s.id === "data-analytics" && (
                  <>
                    <p>
                      By capturing end-to-end data, CellCX enables:
                    </p>
                    <br />
                    <ul>
                      <li>- Tracking patient milestones and bottlenecks.</li>
                      <li>- Analyzing turnaround times and lab utilization.</li>
                      <li>- Predicting demand for manufacturing slots and staffing.</li>
                      <li>- Supporting regulatory reporting and audits.</li>
                    </ul>
                    <br />
                    <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                      <p className="m-0 text-sm"><span className="font-semibold">Impact:</span> According to McKinsey, operational bottlenecks are a primary barrier to scaling therapies [3].</p>
                    </div>
                  </>
                )}
                {s.id === "preparing-scale" && (
                  <>
                    <p>
                      Health systems must adopt digital platforms to scale effectively. The roadmap includes:
                    </p>
                    <br />
                    <ol>
                      <li>1. Audit current workflows and compliance gaps.</li>
                      <li>2. Configure CellCX to embed best practices or replicate organizational processes.</li>
                      <li>3. Standardize workflows across sites.</li>
                      <li>4. Automate communication and scheduling.</li>
                      <li>5. Monitor outcomes and continuously improve with analytics.</li>
                    </ol>
                    <br />
                    <p>
                      This ensures both compliance and scalability.
                    </p>
                  </>
                )}
                {s.id === "conclusion" && (
                  <>
                    <p>
                      Cell and gene therapies have transformative potential, but scaling them requires addressing complex operational challenges. By providing structured workflows, knowledge transfer, and powerful analytics, CellCX enables organizations to expand access to these life-saving therapies safely and efficiently.
                    </p>
                    <button
                      onClick={() => setDownloadFormOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download Full Paper
                    </button>
                  </>
                )}
                {s.id === "references" && (
                  <>
                    <ol>
                      <li>1. Grand View Research. CAR-T Cell Therapy Market Size & Forecast. 2023.</li>
                      <li>2. Alliance for Regenerative Medicine. Annual Report, 2023.</li>
                      <li>3. McKinsey & Company. Cell and Gene Therapy Operations: Overcoming Bottlenecks. 2022.</li>
                    </ol>
                    <div className="mt-6 rounded-xl border border-slate-200 p-4">
                      <h4 className="text-lg font-semibold mb-2">📈 Appendix: Charts and Visuals</h4>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li>• Global CAR-T Market Growth (USD B)</li>
                        <li>• Global Cell & Gene Therapy Clinical Trials</li>
                        <li>• Key Bottlenecks in Cell Therapy Operations</li>
                      </ul>
                    </div>

                    {/* Charts and Visuals Images */}
                    <div className="mt-6 space-y-6">
                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Global CAR-T Market Growth</h5>
                        <img 
                          src="/Image/WhitePapers/CellCX_WhitePaper/image1.png" 
                          alt="Global CAR-T Market Growth Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Global Cell & Gene Therapy Clinical Trials</h5>
                        <img 
                          src="/Image/WhitePapers/CellCX_WhitePaper/image2.png" 
                          alt="Global Cell & Gene Therapy Clinical Trials Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Key Bottlenecks in Cell Therapy Operations</h5>
                        <img 
                          src="/Image/WhitePapers/CellCX_WhitePaper/image3.png" 
                          alt="Key Bottlenecks in Cell Therapy Operations Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          ))}
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-4 lg:pl-2">
          <div className="lg:sticky lg:top-32 space-y-6">
            {/* Summary card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Summary</div>
              <p className="mt-2 text-sm text-slate-700">
                This white paper explores how digital platforms enable safe, efficient, and compliant scaling of cell and gene therapy programs across health systems.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>$3.5B to $23B CAR-T market growth by 2030</li>
                <li>2,000+ active clinical trials worldwide</li>
                <li>Flexible deployment options</li>
                <li>Analytics-driven operational planning</li>
              </ul>
              <button
                onClick={() => setDownloadFormOpen(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download PDF
              </button>
            </div>

            {/* TOC */}
            <nav className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Table of Contents</div>
              <ul className="mt-3 space-y-2">
                {sections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollTo(s.id)}
                      className={
                        "text-left text-sm w-full rounded-lg px-2 py-1 hover:bg-slate-50 " +
                        (activeId === s.id ? "font-semibold text-slate-900" : "text-slate-700")
                      }
                    >
                      {s.id === "exec-summary" && "Executive Summary"}
                      {s.id === "rise-therapy" && "The Rise of Cell & Gene Therapy"}
                      {s.id === "operational-challenges" && "Operational Challenges"}
                      {s.id === "flexible-deployment" && "Flexible Deployment of Best Practices"}
                      {s.id === "data-analytics" && "Data & Analytics in Cell Therapy"}
                      {s.id === "preparing-scale" && "Preparing for Scale"}
                      {s.id === "conclusion" && "Conclusion"}
                      {s.id === "references" && "References"}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Citation */}
            <div id="cite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Citation</div>
              <p className="mt-2 text-xs text-slate-700">
                EceptionistCX Research Group. "Scaling Cell & Gene Therapy Programs: How Technology Enables Safe, Efficient, and Compliant Workflows." August 25, 2025.
              </p>
              <button
                onClick={() => {
                  const text = 'EceptionistCX Research Group. "Scaling Cell & Gene Therapy Programs: How Technology Enables Safe, Efficient, and Compliant Workflows." August 25, 2025.'
                  navigator.clipboard?.writeText(text)
                }}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                Copy Citation
              </button>
            </div>
          </div>
        </aside>
      </main>

      {/* Related white papers */}
      <section className="border-t border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto max-w-6xl px-4">
          <h3 className="text-xl md:text-2xl font-semibold text-slate-900">Related White Papers</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: "Breaking Silos with Integrated Care Coordination", description: "How integrated platforms improve outcomes and reduce costs across healthcare systems.", route: "/whitepapers/integrated-care" },
              { title: "AI in Healthcare Operations", description: "Practical applications for smarter collaboration and better care delivery.", route: "/whitepapers/ai-in-healthcare-operations" },
              { title: "The Future of Referral Management in Healthcare", description: "From fax to AI-powered workflows that eliminate administrative waste.", route: "/whitepapers/referral-cx" }
            ].map((card, index: number) => (
              <button
                key={index}
                onClick={() => navigate(card.route)}
                className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow text-left w-full"
              >
                <div className="flex items-start justify-between gap-4">
                  <h4 className="text-base font-semibold text-slate-900 group-hover:underline">
                    {card.title}
                  </h4>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white">→</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{card.description}</p>
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button 
              onClick={() => navigate('/whitepapers')}
              className="text-sm font-medium text-slate-700 hover:underline"
            >
              ← Back to White Papers
            </button>
          </div>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="h-8" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="Scaling Cell & Gene Therapy Programs: How Technology Enables Safe, Efficient, and Compliant Workflows"
        pdfPath="/PDF/WhitePapers/CellCX_WhitePaper.pdf"
      />
    </div>
  )
}

export default CellCX
